import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Paket 1 — Admin Audit Log görüntüleme
 * Kim, ne zaman, ne değiştirdi. Son 200 kayıt gösterilir.
 * Kullanıcıya göre / aksiyona göre filtre.
 */
const ACTION_LABELS = {
  adjust_balance: 'Bakiye Ayarla',
  credit_add: 'Kredi Ekle',
  credit_deduct: 'Kredi Düş',
  add_position: 'Pozisyon Aç',
  delete_position: 'Pozisyon Kapat',
  update_position: 'Pozisyon Güncelle',
  add_completed_tx_display: 'Kapalı İşlem Ekle (display)',
  update_completed_tx_display: 'Kapalı İşlem Güncelle (display)',
  delete_completed_tx_display: 'Kapalı İşlem Sil (display)',
  deposit_approve: 'Yatırım Onayla',
  deposit_reject: 'Yatırım Reddet',
  withdrawal_approve: 'Çekim Onayla',
  withdrawal_reject: 'Çekim Reddet',
  reopen_undo: 'Reopen (Kapanış Undo)',
  ban_user: 'Kullanıcı Banla',
  unban_user: 'Ban Kaldır',
  soft_delete_user: 'Kullanıcı Sil (Soft)',
  create_user: 'Kullanıcı Oluştur',
  recalculate_locked_balance: 'Kilit Hesabı Yenile',
};

function AuditLogViewer() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ target_user_id: '', action: '' });
  const [limit, setLimit] = useState(100);

  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { limit };
      if (filters.target_user_id) params.target_user_id = filters.target_user_id;
      if (filters.action) params.action = filters.action;
      const res = await axios.get(`${API}/admin/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setItems(res.data.items || []);
    } catch (err) {
      console.error('audit-log fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
  }, []);

  const formatTs = (iso) => {
    try {
      return new Date(iso).toLocaleString('tr-TR');
    } catch {
      return iso;
    }
  };

  const formatMoney = (v) => {
    if (v === null || v === undefined) return '—';
    return `₺${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-900 min-h-screen text-white" data-testid="audit-log-viewer">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold">🔍 Denetim Kaydı (Audit Log)</h1>
        <button
          onClick={fetchAuditLog}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50"
          data-testid="audit-log-refresh-btn"
        >
          {loading ? 'Yükleniyor…' : '🔄 Yenile'}
        </button>
      </div>

      {/* Filtreler */}
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input
          type="text"
          placeholder="Kullanıcı ID filtresi"
          value={filters.target_user_id}
          onChange={(e) => setFilters({ ...filters, target_user_id: e.target.value })}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm"
          data-testid="audit-log-filter-user"
        />
        <select
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm"
          data-testid="audit-log-filter-action"
        >
          <option value="">Tüm aksiyonlar</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          <option value={50}>Son 50</option>
          <option value={100}>Son 100</option>
          <option value={200}>Son 200</option>
          <option value={500}>Son 500</option>
        </select>
        <button
          onClick={fetchAuditLog}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-sm"
          data-testid="audit-log-apply-filter-btn"
        >
          Filtrele
        </button>
      </div>

      {/* Kayıtlar */}
      <div className="bg-gray-800 rounded-lg overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-gray-700 sticky top-0">
            <tr>
              <th className="text-left p-2 sm:p-3">Zaman</th>
              <th className="text-left p-2 sm:p-3">Admin</th>
              <th className="text-left p-2 sm:p-3">Aksiyon</th>
              <th className="text-left p-2 sm:p-3">Kullanıcı</th>
              <th className="text-right p-2 sm:p-3">Bakiye Önce</th>
              <th className="text-right p-2 sm:p-3">Bakiye Sonra</th>
              <th className="text-left p-2 sm:p-3">Detay</th>
            </tr>
          </thead>
          <tbody data-testid="audit-log-tbody">
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="text-center p-8 text-gray-500">
                  Kayıt bulunamadı. Bir admin işlemi yapıldığında burada listelenir.
                </td>
              </tr>
            )}
            {items.map((row) => {
              const delta = row.balance_after !== null && row.balance_before !== null
                ? row.balance_after - row.balance_before
                : null;
              return (
                <tr key={row.id} className="border-b border-gray-700 hover:bg-gray-750">
                  <td className="p-2 sm:p-3 whitespace-nowrap text-gray-300">{formatTs(row.ts)}</td>
                  <td className="p-2 sm:p-3 text-blue-300">{row.admin_email || row.admin_id}</td>
                  <td className="p-2 sm:p-3">
                    <span className="px-2 py-1 rounded bg-purple-900/40 text-purple-300 text-xs">
                      {ACTION_LABELS[row.action] || row.action}
                    </span>
                  </td>
                  <td className="p-2 sm:p-3 text-yellow-300">
                    {row.target_user_email || row.target_user_id || '—'}
                  </td>
                  <td className="p-2 sm:p-3 text-right text-gray-400">{formatMoney(row.balance_before)}</td>
                  <td className="p-2 sm:p-3 text-right">
                    <div>{formatMoney(row.balance_after)}</div>
                    {delta !== null && Math.abs(delta) > 0.005 && (
                      <div className={`text-xs ${delta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {delta >= 0 ? '+' : ''}{formatMoney(delta)}
                      </div>
                    )}
                  </td>
                  <td className="p-2 sm:p-3 text-gray-400 max-w-xs">
                    <details>
                      <summary className="cursor-pointer text-xs">JSON</summary>
                      <pre className="text-xs mt-1 whitespace-pre-wrap break-all">
                        {JSON.stringify(row.details, null, 2)}
                      </pre>
                    </details>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Toplam {items.length} kayıt gösteriliyor. Bakiye/pozisyon/kullanıcı değiştiren her admin işlemi burada iz bırakır.
      </div>
    </div>
  );
}

export default AuditLogViewer;
